/**
 * 统一网络服务
 * 整合网络请求、错误处理、重试机制和缓存
 */

/* eslint-disable max-classes-per-file */
import { BaseManager } from './BaseManager';
import { createCache, CacheStrategy } from './CacheManager';
import { handleError } from './ErrorManager';
import logger from '@/utils/logger';

// 请求方法枚举
export enum RequestMethod {
  GET = 'GET',
  POST = 'POST',
  PUT = 'PUT',
  DELETE = 'DELETE',
  PATCH = 'PATCH',
}

// 请求配置接口
export interface RequestConfig {
  url: string;
  method?: RequestMethod;
  headers?: Record<string, string>;
  body?: any;
  timeout?: number;
  retries?: number;
  retryDelay?: number;
  cache?: boolean;
  cacheTTL?: number;
  credentials?: RequestCredentials;
  mode?: RequestMode;
  signal?: AbortSignal;
}

// 响应接口
export interface NetworkResponse<T = any> {
  data: T;
  status: number;
  statusText: string;
  headers: Headers;
  url: string;
  fromCache?: boolean;
  requestTime: number;
  responseTime: number;
}

// 网络错误接口
export interface NetworkError extends Error {
  status?: number;
  statusText?: string;
  url?: string;
  requestConfig?: RequestConfig;
  response?: Response;
}

// 重试策略接口
export interface RetryStrategy {
  shouldRetry(error: NetworkError, attempt: number): boolean;
  getDelay(attempt: number): number;
}

// 请求拦截器接口
export interface RequestInterceptor {
  onRequest?(config: RequestConfig): RequestConfig | Promise<RequestConfig>;
  onRequestError?(error: any): any;
}

// 响应拦截器接口
export interface ResponseInterceptor {
  onResponse?<T>(response: NetworkResponse<T>): NetworkResponse<T> | Promise<NetworkResponse<T>>;
  onResponseError?(error: NetworkError): any;
}

// 网络统计接口

/**
 * 默认重试策略
 */
class DefaultRetryStrategy implements RetryStrategy {
  private maxRetries: number;

  private baseDelay: number;

  constructor(maxRetries = 3, baseDelay = 1000) {
    this.maxRetries = maxRetries;
    this.baseDelay = baseDelay;
  }

  shouldRetry(error: NetworkError, attempt: number): boolean {
    if (attempt >= this.maxRetries) {
      return false;
    }

    // 网络错误或5xx服务器错误才重试
    if (!error.status) {
      return true; // 网络错误
    }

    return error.status >= 500 && error.status < 600;
  }

  getDelay(attempt: number): number {
    // 指数退避策略
    return this.baseDelay * 2 ** (attempt - 1);
  }
}

/**
 * 网络服务管理器
 */
export class NetworkService extends BaseManager {
  private requestInterceptors: RequestInterceptor[] = [];

  private responseInterceptors: ResponseInterceptor[] = [];

  private retryStrategy: RetryStrategy;

  private requestCache = createCache<NetworkResponse>('network-requests', {
    strategy: CacheStrategy.LRU,
    maxSize: 200,
    ttl: 5 * 60 * 1000, // 5分钟
  });

  // 简化的请求计数
  private requestCount = 0;

  private defaultConfig: Partial<RequestConfig> = {
    method: RequestMethod.GET,
    timeout: 10000,
    retries: 3,
    retryDelay: 1000,
    cache: false,
    cacheTTL: 5 * 60 * 1000,
    credentials: 'same-origin',
    mode: 'cors',
  };

  constructor() {
    super({
      name: 'network-service',
    });

    this.retryStrategy = new DefaultRetryStrategy();
  }

  protected async onInit(): Promise<void> {
    logger.info('[NetworkService] 网络服务初始化完成');
  }

  protected async onDestroy(): Promise<void> {
    this.requestInterceptors.length = 0;
    this.responseInterceptors.length = 0;
    this.requestCache.clear();
    logger.info('[NetworkService] 网络服务已销毁');
  }

  /**
   * 添加请求拦截器
   */
  addRequestInterceptor(interceptor: RequestInterceptor): () => void {
    this.requestInterceptors.push(interceptor);

    return () => {
      const index = this.requestInterceptors.indexOf(interceptor);
      if (index > -1) {
        this.requestInterceptors.splice(index, 1);
      }
    };
  }

  /**
   * 添加响应拦截器
   */
  addResponseInterceptor(interceptor: ResponseInterceptor): () => void {
    this.responseInterceptors.push(interceptor);

    return () => {
      const index = this.responseInterceptors.indexOf(interceptor);
      if (index > -1) {
        this.responseInterceptors.splice(index, 1);
      }
    };
  }

  /**
   * 设置重试策略
   */
  setRetryStrategy(strategy: RetryStrategy): void {
    this.retryStrategy = strategy;
  }

  /**
   * 设置默认配置
   */
  setDefaultConfig(config: Partial<RequestConfig>): void {
    this.defaultConfig = { ...this.defaultConfig, ...config };
  }

  /**
   * 发送请求
   */
  async request<T = any>(config: RequestConfig): Promise<NetworkResponse<T>> {
    const finalConfig = { ...this.defaultConfig, ...config };

    // 检查缓存
    if (finalConfig.cache && finalConfig.method === RequestMethod.GET) {
      const cacheKey = this.getCacheKey(finalConfig);
      const cached = this.requestCache.get(cacheKey);
      if (cached) {
        logger.debug(`[NetworkService] 使用缓存响应: ${finalConfig.url}`);
        return { ...cached, fromCache: true } as NetworkResponse<T>;
      }
    }

    let lastError: NetworkError;
    let attempt = 0;

    while (attempt <= (finalConfig.retries || 0)) {
      try {
        // eslint-disable-next-line no-await-in-loop
        const response = await this.executeRequest<T>(finalConfig);

        // 缓存成功的GET请求
        if (
          finalConfig.cache &&
          finalConfig.method === RequestMethod.GET &&
          response.status < 400
        ) {
          const cacheKey = this.getCacheKey(finalConfig);
          this.requestCache.set(cacheKey, response, finalConfig.cacheTTL);
        }

        this.requestCount++;
        return response;
      } catch (error) {
        lastError = error as NetworkError;
        attempt++;

        if (
          attempt <= (finalConfig.retries || 0) &&
          this.retryStrategy.shouldRetry(lastError, attempt)
        ) {
          const delay = this.retryStrategy.getDelay(attempt);
          logger.warn(
            `[NetworkService] 请求失败，${delay}ms后重试 (${attempt}/${finalConfig.retries}): ${finalConfig.url}`,
            lastError
          );

          // eslint-disable-next-line no-await-in-loop
          await this.sleep(delay);
        } else {
          break;
        }
      }
    }

    // 处理错误
    await handleError(lastError!, {
      source: 'network',
      url: finalConfig.url,
      method: finalConfig.method,
    });

    throw new Error(lastError!.message || 'Network request failed');
  }

  /**
   * GET请求
   */
  async get<T = any>(url: string, config?: Partial<RequestConfig>): Promise<NetworkResponse<T>> {
    return this.request<T>({ ...config, url, method: RequestMethod.GET });
  }

  /**
   * POST请求
   */
  async post<T = any>(
    url: string,
    body?: any,
    config?: Partial<RequestConfig>
  ): Promise<NetworkResponse<T>> {
    return this.request<T>({ ...config, url, method: RequestMethod.POST, body });
  }

  /**
   * PUT请求
   */
  async put<T = any>(
    url: string,
    body?: any,
    config?: Partial<RequestConfig>
  ): Promise<NetworkResponse<T>> {
    return this.request<T>({ ...config, url, method: RequestMethod.PUT, body });
  }

  /**
   * DELETE请求
   */
  async delete<T = any>(url: string, config?: Partial<RequestConfig>): Promise<NetworkResponse<T>> {
    return this.request<T>({ ...config, url, method: RequestMethod.DELETE });
  }

  /**
   * PATCH请求
   */
  async patch<T = any>(
    url: string,
    body?: any,
    config?: Partial<RequestConfig>
  ): Promise<NetworkResponse<T>> {
    return this.request<T>({ ...config, url, method: RequestMethod.PATCH, body });
  }

  /**
   * 执行实际请求
   */
  private async executeRequest<T>(config: RequestConfig): Promise<NetworkResponse<T>> {
    const requestTime = Date.now();

    // 应用请求拦截器
    let finalConfig = config;
    for (const interceptor of this.requestInterceptors) {
      if (interceptor.onRequest) {
        try {
          // eslint-disable-next-line no-await-in-loop
          finalConfig = await interceptor.onRequest(finalConfig);
        } catch (error) {
          if (interceptor.onRequestError) {
            interceptor.onRequestError(error);
          }
          throw error;
        }
      }
    }

    // 准备请求选项
    const fetchOptions: RequestInit = {
      method: finalConfig.method,
      headers: finalConfig.headers,
      credentials: finalConfig.credentials,
      mode: finalConfig.mode,
      signal: finalConfig.signal,
    };

    // 处理请求体
    if (finalConfig.body) {
      if (typeof finalConfig.body === 'object' && !(finalConfig.body instanceof FormData)) {
        fetchOptions.body = JSON.stringify(finalConfig.body);
        fetchOptions.headers = {
          'Content-Type': 'application/json',
          ...fetchOptions.headers,
        };
      } else {
        fetchOptions.body = finalConfig.body;
      }
    }

    // 设置超时
    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
      controller.abort();
    }, finalConfig.timeout);

    if (!finalConfig.signal) {
      fetchOptions.signal = controller.signal;
    }

    try {
      const response = await fetch(finalConfig.url, fetchOptions);
      clearTimeout(timeoutId);

      const responseTime = Date.now();

      // 检查响应状态
      if (!response.ok) {
        const error: NetworkError = new Error(
          `HTTP ${response.status}: ${response.statusText}`
        ) as NetworkError;
        error.status = response.status;
        error.statusText = response.statusText;
        error.url = finalConfig.url;
        error.requestConfig = finalConfig;
        error.response = response;
        throw error;
      }

      // 解析响应数据
      let data: T;
      const contentType = response.headers.get('content-type');

      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else if (contentType && contentType.includes('text/')) {
        data = (await response.text()) as any;
      } else {
        data = (await response.blob()) as any;
      }

      const networkResponse: NetworkResponse<T> = {
        data,
        status: response.status,
        statusText: response.statusText,
        headers: response.headers,
        url: finalConfig.url,
        requestTime,
        responseTime,
      };

      // 应用响应拦截器
      let finalResponse = networkResponse;
      for (const interceptor of this.responseInterceptors) {
        if (interceptor.onResponse) {
          try {
            // eslint-disable-next-line no-await-in-loop
            finalResponse = await interceptor.onResponse(finalResponse);
          } catch (error) {
            if (interceptor.onResponseError) {
              interceptor.onResponseError(error as NetworkError);
            }
            throw error;
          }
        }
      }

      return finalResponse;
    } catch (error) {
      clearTimeout(timeoutId);

      // 应用响应错误拦截器
      for (const interceptor of this.responseInterceptors) {
        if (interceptor.onResponseError) {
          try {
            interceptor.onResponseError(error as NetworkError);
          } catch (interceptorError) {
            logger.error('[NetworkService] 响应拦截器错误:', interceptorError);
          }
        }
      }

      // 包装网络错误
      if (error instanceof Error) {
        const networkError = error as NetworkError;
        networkError.url = finalConfig.url;
        networkError.requestConfig = finalConfig;
        throw networkError;
      }

      const errorMessage = error instanceof Error ? error.message : String(error);
      throw new Error(errorMessage);
    }
  }

  /**
   * 生成缓存键
   */
  private getCacheKey(config: RequestConfig): string {
    const key = `${config.method}:${config.url}`;
    if (config.body) {
      const bodyHash = this.hashCode(JSON.stringify(config.body));
      return `${key}:${bodyHash}`;
    }
    return key;
  }

  /**
   * 简单哈希函数
   */
  private hashCode(str: string): number {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash &= hash; // 转换为32位整数
    }
    return hash;
  }

  /**
   * 睡眠函数
   */
  private sleep(ms: number): Promise<void> {
    return new Promise((resolve) => {
      setTimeout(resolve, ms);
    });
  }

  /**
   * 获取请求计数
   */
  getRequestCount(): number {
    return this.requestCount;
  }

  /**
   * 清理缓存
   */
  clearCache(): void {
    this.requestCache.clear();
    logger.debug('[NetworkService] 请求缓存已清理');
  }

  /**
   * 重置计数
   */
  resetCount(): void {
    this.requestCount = 0;
    logger.debug('[NetworkService] 请求计数已重置');
  }
}

// 导出单例实例
export const networkService = new NetworkService();

// 导出便捷函数
export const request = <T = any>(config: RequestConfig) => networkService.request<T>(config);

export const get = <T = any>(url: string, config?: Partial<RequestConfig>) =>
  networkService.get<T>(url, config);

export const post = <T = any>(url: string, body?: any, config?: Partial<RequestConfig>) =>
  networkService.post<T>(url, body, config);

export const put = <T = any>(url: string, body?: any, config?: Partial<RequestConfig>) =>
  networkService.put<T>(url, body, config);

export const del = <T = any>(url: string, config?: Partial<RequestConfig>) =>
  networkService.delete<T>(url, config);

export const patch = <T = any>(url: string, body?: any, config?: Partial<RequestConfig>) =>
  networkService.patch<T>(url, body, config);

// 带超时的fetch函数（向后兼容）
export const fetchWithTimeout = async (
  url: string,
  options: RequestInit & { timeout?: number } = {}
): Promise<Response> => {
  const { timeout = 10000, ...fetchOptions } = options;

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, {
      ...fetchOptions,
      signal: controller.signal,
    });
    clearTimeout(timeoutId);
    return response;
  } catch (error) {
    clearTimeout(timeoutId);
    throw error;
  }
};
